export type WordDiffPartType = 'added' | 'removed' | 'common';

export interface WordDiffPart {
  value: string;
  type: WordDiffPartType;
}

/**
 * Above this per-side character length the word diff is skipped: it runs an
 * O(n·m) longest-common-subsequence pass synchronously during render, which can
 * freeze the tab on very long input. Callers should fall back to rendering both
 * columns as plain text when {@link isDiffInputTooLarge} returns `true`.
 */
export const MAX_DIFF_INPUT_LENGTH = 4000;

export const isDiffInputTooLarge = (original: string, improved: string): boolean =>
  original.length > MAX_DIFF_INPUT_LENGTH || improved.length > MAX_DIFF_INPUT_LENGTH;

/**
 * Matches a `{{ placeholder }}` token as a single unit (mirrors `VARIABLE_REGEX`
 * in `@dms/shared`). The diff keeps placeholders atomic so it never splits one
 * across parts — otherwise the chip renderer would see broken `{{` / `}}`
 * fragments and fail to recognise it. Non-global so `.test` stays stateless and
 * `.split` keeps the whole-token delimiter.
 */
const PLACEHOLDER_TOKEN_REGEX = /(\{\{\s*[\w.]+\s*\}\})/;
const WORD_REGEX = /\s+|\S+/g;

/**
 * Splits text into placeholder, word and whitespace tokens, preserving
 * everything so the original text can be reconstructed by concatenation.
 * `{{ ... }}` placeholders are emitted as single tokens (even when glued to a
 * word), so the word diff treats each one as an indivisible unit.
 */
const tokenize = (text: string): string[] =>
  text
    .split(PLACEHOLDER_TOKEN_REGEX)
    .filter((segment) => segment.length > 0)
    .flatMap((segment) =>
      PLACEHOLDER_TOKEN_REGEX.test(segment) ? [segment] : (segment.match(WORD_REGEX) ?? [])
    );

const lcsKey = (i: number, j: number): string => `${i},${j}`;

/**
 * Builds the longest-common-subsequence length table for two token arrays as a
 * Map keyed by `i,j`. `table[i,j]` is the LCS length of `a[i..]` and `b[j..]`.
 */
const buildLcsTable = (a: string[], b: string[]): Map<string, number> => {
  const table = new Map<string, number>();
  const get = (i: number, j: number): number => table.get(lcsKey(i, j)) ?? 0;

  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      const value =
        a.at(i) === b.at(j) ? get(i + 1, j + 1) + 1 : Math.max(get(i + 1, j), get(i, j + 1));
      table.set(lcsKey(i, j), value);
    }
  }

  return table;
};

/**
 * Collapses consecutive parts of the same type into a single part so colored
 * runs render as whole phrases rather than token-by-token fragments.
 */
const mergeAdjacentParts = (parts: WordDiffPart[]): WordDiffPart[] =>
  parts.reduce<WordDiffPart[]>((accumulator, part) => {
    const previous = accumulator.at(-1);

    if (previous?.type === part.type) {
      previous.value += part.value;
    } else {
      accumulator.push({...part});
    }

    return accumulator;
  }, []);

/**
 * Computes a word-level diff between the original and improved text using a
 * longest-common-subsequence walk. Each part is tagged `added` (only in
 * improved), `removed` (only in original) or `common` (in both).
 *
 * The original column should render `common` + `removed` parts; the improved
 * column should render `common` + `added` parts.
 */
export const getWordDiff = (original: string, improved: string): WordDiffPart[] => {
  const a = tokenize(original);
  const b = tokenize(improved);
  const table = buildLcsTable(a, b);
  const get = (i: number, j: number): number => table.get(lcsKey(i, j)) ?? 0;

  const parts: WordDiffPart[] = [];
  let i = 0;
  let j = 0;

  while (i < a.length && j < b.length) {
    if (a.at(i) === b.at(j)) {
      parts.push({value: a.at(i) ?? '', type: 'common'});
      i++;
      j++;
    } else if (get(i + 1, j) >= get(i, j + 1)) {
      parts.push({value: a.at(i) ?? '', type: 'removed'});
      i++;
    } else {
      parts.push({value: b.at(j) ?? '', type: 'added'});
      j++;
    }
  }

  while (i < a.length) {
    parts.push({value: a.at(i) ?? '', type: 'removed'});
    i++;
  }

  while (j < b.length) {
    parts.push({value: b.at(j) ?? '', type: 'added'});
    j++;
  }

  return mergeAdjacentParts(parts);
};
